import React, { useState, useMemo } from 'react';
import {
  Save,
  RotateCcw,
  ShieldCheck,
  AlertTriangle,
  Wand2,
  BookmarkPlus,
  Download,
  Upload,
  Check,
  Search,
  Sliders,
  Eye,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import type { ThemeTokens, ThemePreset, SavedCustomTheme } from '../../types/theme';
import { THEME_CATEGORIES, THEME_PRESETS, DEFAULT_DARK_TOKENS } from '../../lib/themePresets';
import { checkThemeContrast, autoFixThemeContrast } from '../../utils/contrast';
import { LiveThemePreview } from './LiveThemePreview';
import { PresetSelector } from './PresetSelector';
import { ThemeCategoryAccordion } from './ThemeCategoryAccordion';

interface ThemeStudioProps {
  onSaved?: () => void;
}

export const ThemeStudio: React.FC<ThemeStudioProps> = ({ onSaved }) => {
  const {
    customTokens,
    setCustomTokens,
    setTheme,
    activePresetId,
    applyPreset,
    savedCustomThemes,
    saveCustomTheme,
    deleteCustomTheme,
    resetCustomTheme,
  } = useTheme();

  // Local draft state for real-time live preview before final apply/save if desired,
  // or synchronized with customTokens
  const [draftTokens, setDraftTokens] = useState<ThemeTokens>(customTokens);
  const [searchFilter, setSearchFilter] = useState('');
  const [saveNameModalOpen, setSaveNameModalOpen] = useState(false);
  const [customThemeName, setCustomThemeName] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showLivePreviewSticky, setShowLivePreviewSticky] = useState(true);

  // Sync draftTokens whenever customTokens changes from external preset selection
  React.useEffect(() => {
    setDraftTokens(customTokens);
  }, [customTokens]);

  const activePreset: ThemePreset = useMemo(() => {
    return THEME_PRESETS.find((p) => p.id === activePresetId) || THEME_PRESETS[1] || THEME_PRESETS[0]!;
  }, [activePresetId]);

  const contrastIssues = useMemo(() => {
    return checkThemeContrast(draftTokens);
  }, [draftTokens]);

  const handleTokenChange = (key: keyof ThemeTokens, value: string) => {
    const next = { ...draftTokens, [key]: value };
    setDraftTokens(next);
    // Real-time update to theme context so the live preview and entire app respond immediately
    setCustomTokens(next);
    setTheme('custom');
  };

  const handleResetCategory = (keys: Array<keyof ThemeTokens>) => {
    const next = { ...draftTokens };
    const base = activePreset.tokens;
    keys.forEach((k) => {
      next[k] = base[k] || DEFAULT_DARK_TOKENS[k];
    });
    setDraftTokens(next);
    setCustomTokens(next);
  };

  const handleSelectPreset = (preset: ThemePreset) => {
    applyPreset(preset.id);
    setDraftTokens(preset.tokens);
    setSuccessMessage(`Applied preset: ${preset.name}`);
    setTimeout(() => setSuccessMessage(null), 2500);
  };

  const handleSelectSavedTheme = (saved: SavedCustomTheme) => {
    setDraftTokens(saved.tokens);
    setCustomTokens(saved.tokens);
    setTheme('custom');
    setSuccessMessage(`Loaded saved theme: ${saved.name}`);
    setTimeout(() => setSuccessMessage(null), 2500);
  };

  const handleAutoFixAllContrast = () => {
    const fixed = autoFixThemeContrast(draftTokens);
    setDraftTokens(fixed);
    setCustomTokens(fixed);
    setSuccessMessage('Auto-adjusted colors to satisfy WCAG AA contrast standards!');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleSaveAsPreset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customThemeName.trim()) return;
    saveCustomTheme(customThemeName, draftTokens);
    setCustomThemeName('');
    setSaveNameModalOpen(false);
    setSuccessMessage('Custom theme saved to your library!');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleResetAll = () => {
    if (confirm('Reset all theme customizations back to default preset?')) {
      resetCustomTheme();
      setDraftTokens(DEFAULT_DARK_TOKENS);
      setSuccessMessage('Reset customizations to default theme.');
      setTimeout(() => setSuccessMessage(null), 2500);
    }
  };

  const handleExportJson = () => {
    const jsonStr = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(
        {
          version: '1.0',
          name: activePreset.name || 'Custom Theme',
          tokens: draftTokens,
        },
        null,
        2
      )
    )}`;
    const dlAnchorElem = document.createElement('a');
    dlAnchorElem.setAttribute('href', jsonStr);
    dlAnchorElem.setAttribute('download', `sahaj-theme-${Date.now()}.json`);
    dlAnchorElem.click();
  };

  const handleImportJson = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        if (parsed.tokens) {
          const merged = { ...draftTokens, ...parsed.tokens };
          setDraftTokens(merged);
          setCustomTokens(merged);
          setTheme('custom');
          setSuccessMessage(`Successfully imported theme: ${parsed.name || 'Custom'}`);
          setTimeout(() => setSuccessMessage(null), 3000);
        }
      } catch (err) {
        alert('Invalid theme JSON file format.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Filter categories by search keyword
  const filteredCategories = useMemo(() => {
    if (!searchFilter.trim()) return THEME_CATEGORIES;
    const q = searchFilter.toLowerCase();
    return THEME_CATEGORIES.map((cat) => {
      const matchingFields = cat.fields.filter(
        (f) =>
          f.label.toLowerCase().includes(q) ||
          f.description.toLowerCase().includes(q) ||
          f.key.toLowerCase().includes(q)
      );
      return { ...cat, fields: matchingFields };
    }).filter((cat) => cat.fields.length > 0);
  }, [searchFilter]);

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Top Banner / Feedback Message */}
      {successMessage && (
        <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold rounded-2xl flex items-center gap-2 animate-in fade-in slide-in-from-top-1">
          <Check className="h-4 w-4 flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Preset Selector */}
      <div className="p-5 rounded-3xl bg-muted/20 border border-border/80 space-y-4">
        <PresetSelector
          activePresetId={activePresetId}
          savedCustomThemes={savedCustomThemes}
          onSelectPreset={handleSelectPreset}
          onSelectSavedTheme={handleSelectSavedTheme}
          onDeleteSavedTheme={deleteCustomTheme}
        />
      </div>

      {/* Live Preview & Contrast Health Summary */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-xs text-foreground uppercase tracking-wider">
            <Eye className="h-3.5 w-3.5 text-primary" />
            <span>Interactive Live Preview</span>
          </div>

          <button
            type="button"
            onClick={() => setShowLivePreviewSticky(!showLivePreviewSticky)}
            className="text-[11px] font-semibold text-muted-foreground hover:text-foreground transition flex items-center gap-1"
          >
            {showLivePreviewSticky ? 'Collapse Preview' : 'Expand Preview'}
          </button>
        </div>

        {showLivePreviewSticky && <LiveThemePreview tokens={draftTokens} />}

        {/* Accessibility Contrast Health Bar */}
        <div
          className={`p-3.5 rounded-2xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs ${
            contrastIssues.length === 0
              ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-700 dark:text-emerald-300'
              : 'bg-amber-500/5 border-amber-500/20 text-amber-800 dark:text-amber-300'
          }`}
        >
          <div className="flex items-center gap-2.5">
            {contrastIssues.length === 0 ? (
              <ShieldCheck className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
            )}
            <div>
              <span className="font-bold">
                {contrastIssues.length === 0
                  ? 'All critical UI color pairs satisfy WCAG 2.1 AA accessibility standards!'
                  : `${contrastIssues.length} color pair${
                      contrastIssues.length > 1 ? 's have' : ' has'
                    } low contrast ratio.`}
              </span>
              {contrastIssues.length > 0 && (
                <p className="text-[11px] opacity-80 mt-0.5">
                  Some labels might be difficult to read on certain backgrounds.
                </p>
              )}
            </div>
          </div>

          {contrastIssues.length > 0 && (
            <button
              type="button"
              onClick={handleAutoFixAllContrast}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-amber-500 text-white font-bold text-xs hover:bg-amber-600 transition shadow-xs flex-shrink-0"
            >
              <Wand2 className="h-3.5 w-3.5" />
              Auto-Fix All Contrast
            </button>
          )}
        </div>
      </div>

      {/* Main Studio Controls Header & Search */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
        <div className="flex items-center gap-2">
          <Sliders className="h-4 w-4 text-primary" />
          <h4 className="font-bold text-sm text-foreground">Theme Studio Fine-Tuning</h4>
        </div>

        <div className="relative w-full sm:w-64">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Filter tokens (e.g. sidebar, chat)..."
            className="w-full h-8 pl-8 pr-3 bg-background border border-border rounded-xl text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
          />
        </div>
      </div>

      {/* Accordion Categories */}
      <div className="space-y-3">
        {filteredCategories.length === 0 ? (
          <div className="p-8 text-center bg-card rounded-2xl border border-border text-muted-foreground text-xs">
            No design tokens match "{searchFilter}".
          </div>
        ) : (
          filteredCategories.map((cat, idx) => (
            <ThemeCategoryAccordion
              key={cat.key}
              config={cat}
              tokens={draftTokens}
              defaultTokens={activePreset.tokens}
              onChangeToken={handleTokenChange}
              onResetCategory={handleResetCategory}
              defaultOpen={idx === 0 || !!searchFilter.trim()}
            />
          ))
        )}
      </div>

      {/* Global Actions Toolbar */}
      <div className="p-4 rounded-2xl bg-card border border-border flex flex-wrap items-center justify-between gap-3 shadow-xs">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setSaveNameModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-semibold text-xs border border-border transition shadow-xs"
          >
            <BookmarkPlus className="h-3.5 w-3.5 text-primary" />
            <span>Save as My Preset</span>
          </button>

          <button
            type="button"
            onClick={handleExportJson}
            title="Export theme configuration as JSON"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground font-medium text-xs border border-border/80 transition"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Export JSON</span>
          </button>

          <label className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground font-medium text-xs border border-border/80 transition cursor-pointer">
            <Upload className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Import JSON</span>
            <input type="file" accept=".json" onChange={handleImportJson} className="hidden" />
          </label>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleResetAll}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl hover:bg-destructive/10 text-muted-foreground hover:text-destructive font-semibold text-xs transition"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>Reset All</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setCustomTokens(draftTokens);
              setTheme('custom');
              setSuccessMessage('Theme applied globally across all workspace views!');
              setTimeout(() => setSuccessMessage(null), 3000);
              onSaved?.();
            }}
            className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary text-primary-foreground font-bold text-xs rounded-xl hover:bg-primary/90 transition shadow-sm"
          >
            <Save className="h-3.5 w-3.5" />
            <span>Apply Changes</span>
          </button>
        </div>
      </div>

      {/* Modal: Save as Custom Theme */}
      {saveNameModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-card border border-border rounded-3xl shadow-2xl p-5 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-2 font-bold text-base text-foreground">
              <BookmarkPlus className="h-5 w-5 text-primary" />
              <span>Save as Custom Theme</span>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Name your custom color scheme to easily restore or switch to it at any time.
            </p>

            <form onSubmit={handleSaveAsPreset} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Theme Name</label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={customThemeName}
                  onChange={(e) => setCustomThemeName(e.target.value)}
                  placeholder="e.g. Cyberpunk Neon or Emerald Slate"
                  className="w-full h-10 px-3.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSaveNameModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-muted-foreground hover:bg-muted transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-xl hover:bg-primary/90 transition shadow-sm"
                >
                  Save Preset
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
